import { PasswordChangeRequest, PasswordGenerationRequest, PasswordUpdateRequest, TeamFileLinkDto, InAppNotificationCreateDto } from 'data/dtos'
import { CompanyModuleAccessDto } from 'data/dtos/CompanyModuleAccessDto'
import { UsernameExistsDto } from 'data/dtos/UsernameExistsDto'
import { ValueTranslationRequestDto, ValueTranslationResponseDto } from 'data/dtos/ValueTranslationDto'
import { AnonymousFormModel, CompanyFormModel, CultureGroupModel, TeamModel, UserModel } from 'data/models'
import { EmailTemplateModel } from 'data/models/EmailTemplateModel'
import { JourneyModel } from 'data/models/JourneyModel'
import { JourneyCheckPointModel } from 'data/models/JourneyCheckPointModel'
import { RasterModel } from 'data/models/RasterModel'
import { ShapeFileModel } from 'data/models/ShapefileModel'
import UploadTypes from 'enums/UploadTypes'
import { apiSettings } from 'etc/settings'
import { Dictionary } from 'types'
import { FormTemplateFilterArguments, IWorkawareApi, PagedArguments, PagedResult } from 'types/apis'
import { getUser } from 'utils/configureStore'
import { requestBuilder } from 'utils/request'
import { AssignmentDto } from 'data/dtos/AssignmentDto'
import { CompanyMessageModel } from 'data/models/CompanyMessageModel'
import { CourseModel } from 'data/models/CourseModel'
import { CounterModel } from 'data/models/CounterModel'
import { TrackingModel } from 'data/models/TrackingModel'
import { CameraDto } from 'data/dtos/CameraDto'
import { DirectMessageModel } from 'data/models/DirectMessageModel'
import { SMSMessageDto } from 'data/dtos/SMSMessageDto'
import { AssignmentStatusDto } from 'data/dtos/AssignmentStatusDto'
import { ScheduledEventTypeDto } from 'data/dtos/ScheduledEventTypeDto'
import { ScheduledStatusTypeDto } from 'data/dtos/ScheduledStatusTypeDto'
import { ScheduledVisibilityTypeDto } from 'data/dtos/ScheduledVisibilityTypeDto'
import { AssignmentReportFilter, AssignmentReportStatsDto } from 'data/dtos/AssignmentReportDto'
import { ScheduledItemReportFilter, ScheduledItemReportStatsDto } from 'data/dtos/ScheduledItemReportDto'
import { JourneyReportFilter, JourneyReportStatsDto } from 'data/dtos/JourneyReportDto'
import { TrainingReportFilter, TrainingReportStatsDto } from 'data/dtos/TrainingReportDto'
import { PolicyReportFilter, PolicyReportStatsDto } from 'data/dtos/PolicyReportDto'
import { AssetMaintenanceReportFilter, AssetMaintenanceReportStatsDto } from 'data/dtos/AssetMaintenanceReportDto'
import { AssetReliabilityReportFilter, AssetReliabilityReportStatsDto } from 'data/dtos/AssetReliabilityReportDto'
import { SiteLocationReportFilter, SiteLocationReportStatsDto } from 'data/dtos/SiteLocationReportDto'
import { ExceptionsRisksReportFilter, ExceptionsRisksReportStatsDto } from 'data/dtos/ExceptionsRisksReportDto'
import { ActivityLogReportFilter, ActivityLogReportStatsDto } from 'data/dtos/ActivityLogReportDto'
import { WorkforcePerformanceReportFilter, WorkforcePerformanceReportStatsDto } from 'data/dtos/WorkforcePerformanceReportDto'
import { ExecutiveOverviewReportFilter, ExecutiveOverviewStatsDto } from 'data/dtos/ExecutiveOverviewReportDto'
import { FormsInspectionsReportFilter, FormsInspectionsReportStatsDto } from 'data/dtos/FormsInspectionsReportDto'
import { RecurringAssignmentDto } from 'data/dtos/RecurringAssignmentDto'
import { ParticipationDashboardByEmployeeRecordRequest } from 'data/models/ParticipationDashboardByEmployeeRecordRequest'
import { ParticipationDashboardByFormDateRequest } from 'data/models/ParticipationDashboardByFormDateRequest'
import { DashboardDataDTO } from 'data/dtos/DashboardDataDTO'

const request = requestBuilder(apiSettings.workaware)

export class WorkawareApi implements IWorkawareApi {
  private _getCompanyId = (): number => getUser()?.profile.company_id
  private _getCurrentUserId = (): number => getUser()?.profile.user_id

  public auth = {
    forgotPassword: (username: string): Promise<any> =>
      request('/api/auth/forgot-password', { method: 'post', data: { username }, silent: true }),
    resetPassword: (token: string, newPassword: string): Promise<any> =>
      request('/api/auth/reset-password', { method: 'post', data: { token, newPassword }, silent: true }),
    verifyTwoFactor: (code: string, sessionToken: string): Promise<any> =>
      request('/api/auth/verify-2fa', { method: 'post', data: { code, sessionToken } }),
  }

  public reporting = {
    utilities: {
      translate: (data: ValueTranslationRequestDto) => request<ValueTranslationResponseDto>(`/api/v2/reporting/translate`, { method: 'post', data }),
      getDashbaordData: (): Promise<any> => request('/api/v2/reporting/GetAggregatedStatsAsync', { method: 'get', silent: true }),
      getUserDashboardData: (): Promise<any> => request('/api/v2/reporting/GetUserDashboardStatsAsync', { method: 'get', silent: true }),
    },
    assignments: {
      getStats: (params: AssignmentReportFilter): Promise<AssignmentReportStatsDto> =>
        request('/api/v2/reporting/assignments/stats', { method: 'get', params, silent: true }),
    },
    scheduledItems: {
      getStats: (params: ScheduledItemReportFilter): Promise<ScheduledItemReportStatsDto> =>
        request('/api/v2/reporting/scheduleditems/stats', { method: 'get', params, silent: true }),
    },
    journeys: {
      getStats: (params: JourneyReportFilter): Promise<JourneyReportStatsDto> =>
        request('/api/v2/reporting/journeys/stats', { method: 'get', params, silent: true }),
    },
    training: {
      getStats: (params: TrainingReportFilter): Promise<TrainingReportStatsDto> =>
        request('/api/v2/reporting/training/stats', { method: 'get', params, silent: true }),
      getCertRecords: (): Promise<any[]> =>
        request('/api/v2/reporting/training/cert-records', { method: 'get', silent: true }),
    },
    policies: {
      getStats: (params: PolicyReportFilter): Promise<PolicyReportStatsDto> =>
        request('/api/v2/reporting/policies/stats', { method: 'get', params, silent: true }),
    },
    assetMaintenance: {
      getStats: (params: AssetMaintenanceReportFilter): Promise<AssetMaintenanceReportStatsDto> =>
        request('/api/v2/reporting/assetmaintenance/stats', { method: 'get', params, silent: true }),
    },
    assetReliability: {
      getStats: (params: AssetReliabilityReportFilter): Promise<AssetReliabilityReportStatsDto> =>
        request('/api/v2/reporting/assetreliability/stats', { method: 'get', params, silent: true }),
    },
    siteLocation: {
      getStats: (params: SiteLocationReportFilter): Promise<SiteLocationReportStatsDto> =>
        request('/api/v2/reporting/sitelocation/stats', { method: 'get', params, silent: true }),
    },
    exceptions: {
      getStats: (params: ExceptionsRisksReportFilter): Promise<ExceptionsRisksReportStatsDto> =>
        request('/api/v2/reporting/exceptions/stats', { method: 'get', params, silent: true }),
    },
    activityLog: {
      getStats: (params: ActivityLogReportFilter): Promise<ActivityLogReportStatsDto> =>
        request('/api/v2/reporting/activitylog/stats', { method: 'get', params, silent: true }),
    },
    workforce: {
      getStats: (params: WorkforcePerformanceReportFilter): Promise<WorkforcePerformanceReportStatsDto> =>
        request('/api/v2/reporting/workforce/stats', { method: 'get', params, silent: true }),
    },
    executive: {
      getStats: (params: ExecutiveOverviewReportFilter): Promise<ExecutiveOverviewStatsDto> =>
        request('/api/v2/reporting/executive/stats', { method: 'get', params, silent: true }),
    },
    forms: {
      getStats: (params: FormsInspectionsReportFilter): Promise<FormsInspectionsReportStatsDto> =>
        request('/api/v2/reporting/forms/stats', { method: 'get', params, silent: true }),
    },
  }

  public calendar = {
    feed: {
      list: () => request('/api/v2/calendars/external', { silent: true }),
      external: (guid: string) => request(`/api/v2/calendar/external/${guid}`, { responseType: 'text', silent: true }),
      allAssignments: (params?: any) => request('/api/v2/calendar/allAssignments', { responseType: 'text', params }),
      allIncompleteAssignments: () => request('/api/v2/calendar/allIncomplete', { responseType: 'text' }),
      myAssignments: () => request('/api/v2/calendar/assignments', { responseType: 'text' }),
      myIncompleteAssignments: () => request('/api/v2/calendar/incompleteAssignments', { responseType: 'text' }),
    },
  }

public  DashboardApi = {
  getStatistics: (): Promise<any> => request('/api/DashboardAPI/GetDashboardStatistics'),
  getSummary: (): Promise<any> => request('/api/DashboardAPI/GetDashboardSummary'),
  getAll: (expand?: string): Promise<any> => request(`/odata/ScheduledItems${expand ? `?$expand=${expand}` : ''}`),
}

public ParticipationDashboard = {
  getByDate: (payload: ParticipationDashboardByFormDateRequest): Promise<any> => 
    request('/api/ParticipationDashboardAPI/GetByDate', {
      method: 'POST',
      data: payload,
    }),

  getByEmployeeRecord: (payload: ParticipationDashboardByEmployeeRecordRequest): Promise<any> => 
    request('/api/ParticipationDashboardAPI/GetByEmployeeRecord', {
      method: 'POST',
      data: payload,
    }),
}


public attachments = {
  delete: (id: number): Promise<any> => request(`/odata/Attachments(${id})`, { method: 'delete' }),
}
public scheduleditems = {
  getAll: (expand?: string): Promise<any> =>
    request(`/odata/ScheduledItems${expand ? `?$expand=${expand}` : ''}`),
  getAllForReport: (): Promise<any> =>
    request(`/odata/ScheduledItems?$select=Id,Subject,StatusId,PriorityId,CreatedOn,CreatedByUserId,IsActive&$expand=ScheduledItemDays($select=Id,Date,ScheduledItemId,IsCompleted,CompanyId)`),
  getForCalendar: (start: string, end: string): Promise<any> =>
    request(`/odata/ScheduledItems?$expand=ScheduledItemDays,SecurityItems&$filter=IsActive eq true and ScheduledItemDays/any(d: d/Date ge ${start} and d/Date le ${end})`),
  getById: (id: number, expand?: string): Promise<any> =>
    request(`/odata/ScheduledItems(${id})${expand ? `?$expand=${expand}` : ''}`),
  create: (data: any): Promise<any> =>request('/odata/ScheduledItems', { method: 'post', data }),
  update: (id: number, data: any): Promise<any> =>request(`/odata/ScheduledItems(${id})`, { method: 'put', data }),
  patch: (id: number, data: Partial<any>): Promise<any> =>request(`/odata/ScheduledItems(${id})`, { method: 'patch', data }),
  delete: (id: number): Promise<any> =>request(`/odata/ScheduledItems(${id})`, { method: 'delete' }),
  createRef: (id: number,navigationProperty: 'ScheduledItemDays' | 'ScheduledItemComments' | 'SecurityItems',link: string): Promise<any> =>request(`/odata/ScheduledItems(${id})/${navigationProperty}/$ref`, {method: 'post',data: link,}),
  sendCreatedEmail: (id: number): Promise<any> => request(`/api/ScheduledItemAPI/SendCreatedEmail/${id}`),
  sendChangedEmail: (id: number): Promise<any> => request(`/api/ScheduledItemAPI/SendChangedEmail/${id}`),
  sendOverdueEmail: (id: number): Promise<any> => request(`/api/ScheduledItemAPI/SendOverdueEmail/${id}`),
  sendNotification: (id: number): Promise<any> => request(`/api/ScheduledItemAPI/SendNotification/${id}`),
  sendRemovedNotification: (id: number, removedUserIds: number[]): Promise<any> =>
    request(`/api/ScheduledItemAPI/SendRemovedNotification/${id}`, { method: 'post', data: removedUserIds }),
  getRelatedRecords: (scheduledItemId: number): Promise<any> =>
    request(`/api/ScheduledItemAPI/RelatedRecords/${scheduledItemId}`),
  addRelatedRecord: (scheduledItemId: number, objectType: number, targetId: number): Promise<any> =>
    request(`/api/ScheduledItemAPI/RelatedRecords/${scheduledItemId}`, { method: 'post', data: { ObjectType: objectType, TargetId: targetId } }),
  removeRelatedRecord: (relationId: number): Promise<any> =>
    request(`/api/ScheduledItemAPI/RelatedRecords/${relationId}`, { method: 'delete' }),
  getAttachments: (scheduledItemId: number): Promise<any> =>
    request(`/api/ScheduledItemAPI/Attachments/${scheduledItemId}`),
  uploadAttachment: (scheduledItemId: number, file: File): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)
    return request(`/api/ScheduledItemAPI/Attachments/${scheduledItemId}/Upload`, { method: 'post', data: formData })
  },
  downloadAttachment: (attachmentId: number): Promise<Blob> =>
    request(`/api/ScheduledItemAPI/Attachments/${attachmentId}/Download`, { responseType: 'blob' }),
  unlinkAttachment: (relationId: number): Promise<any> =>
    request(`/api/ScheduledItemAPI/Attachments/${relationId}`, { method: 'delete' }),
};
public scheduledItemComments = {
  getAll: (): Promise<any> => request('/odata/ScheduledItemComments'),
  getById: (id: number): Promise<any> => request(`/odata/ScheduledItemComments(${id})`),
  create: (data: any): Promise<any> => request('/odata/ScheduledItemComments', { method: 'post', data }),
  update: (id: number, data: any): Promise<any> => request(`/odata/ScheduledItemComments(${id})`, { method: 'put', data }),
  patch: (id: number, data: Partial<any>): Promise<any> => request(`/odata/ScheduledItemComments(${id})`, { method: 'patch', data }),
  delete: (id: number): Promise<any> => request(`/odata/ScheduledItemComments(${id})`, { method: 'delete' }),
}
public scheduledItemDays = {
  getAll: (): Promise<any> => request('/odata/ScheduledItemDays'),
  getById: (id: number): Promise<any> => request(`/odata/ScheduledItemDays(${id})`),
  create: (data: any): Promise<any> => request('/odata/ScheduledItemDays', { method: 'post', data }),
  update: (id: number, data: any): Promise<any> => request(`/odata/ScheduledItemDays(${id})`, { method: 'put', data }),
  patch: (id: number, data: Partial<any>): Promise<any> => request(`/odata/ScheduledItemDays(${id})`, { method: 'patch', data }),
  delete: (id: number): Promise<any> => request(`/odata/ScheduledItemDays(${id})`, { method: 'delete' }),
}


  public assets = {
    overdue: () => request('/odata/CMMSAssetOverdueDTOs'),
    cmmsMaintenances: () => request('/odata/CMMSMaintenances'),
    checkout: () => request('/odata/CMMSAssetCheckedOutDTOs'),
    getList: () => request('/odata/CMMSAssets?$select=Id,Name,Description,Location&$filter=Location ne null'),
    getAllList: () => request('/odata/CMMSAssets?$select=Id,Name,Description,IsActive,CreatedOn,PhotoURL,IsOverdue,MachineHours,Odometer&$expand=CMMSPhotos($select=Id,URL)'),
    // Server-side paging for the Assets List page. `query` holds extra OData clauses ($filter/$orderby).
    getPaged: (skip: number, top?: number, query?: string): Promise<any> =>
      request(`/odata/CMMSAssets?$count=true&$skip=${skip}${top === undefined ? '' : `&$top=${top}`}&$select=Id,Name,Description,IsActive,CreatedOn,PhotoURL,IsOverdue,MachineHours,Odometer&$expand=CMMSPhotos($select=Id,URL)${query ? `&${query}` : ''}`),
    // Id/Name only - feeds the parent-asset dropdown of the Add/Edit drawer without loading full rows.
    getIdNames: (): Promise<any> => request('/odata/CMMSAssets?$select=Id,Name&$orderby=Name'),
    getByMaintenanceScheduleId: (scheduleId: number) => request(`/odata/CMMSAssets?$filter=MaintenanceScheduleId eq ${scheduleId}&$select=Id,Name`),
    getBarcodeTypes: () => request('/api/v2/Assets/BarcodeTypes'),
    getOdometerUnitTypes: () => request('/api/v2/Assets/OdometerUnitTypes'),
    get: (id: any) => request(`/api/v2/assets/${id}`),
    post: (data: any): Promise<any> => request(`/api/v2/assets`, { method: 'post', data }, { 'content-type': 'application/json' }),
    put: (data: any): Promise<any> => request(`/api/v2/assets`, { method: 'put', data }, { 'content-type': 'application/json' }),
    delete: (id: any): Promise<any> => request(`/api/v2/assets/${id}`, { method: 'delete' }),
    sites: {
      getList: () => request('/odata/CMMSSites?$select=Id,Name'),
      getListWithCoords: () => request('/odata/CMMSSites?$select=Id,Name,Latitude,Longitude'),
      getTree: () => request('/api/v2/sitestree'),
      getTypes: () => request('/api/v2/sitetypes'),
      get: (id: any) => request(`/api/v2/sites/${id}`),
      post: (data: any): Promise<any> => request(`/api/v2/sites`, { method: 'post', data }, { 'content-type': 'application/json' }),
      put: (data: any): Promise<any> => request(`/api/v2/sites`, { method: 'put', data }, { 'content-type': 'application/json' }),
      delete: (id: any): Promise<any> => request(`/api/v2/sites/${id}`, { method: 'delete' }),
    },
    photos: {
      upload: (id: any, data: FormData): Promise<any> => request(`/api/v2/assets/${id}/photos`, { method: 'post', data }, { 'Content-Type': 'false' }),
      download: (id: any): Promise<Blob> => request(`/api/v2/assets/photos/${id}`, { responseType: 'blob' }),
      delete: (id: any): Promise<any> => request(`/api/v2/assets/photos/${id}`, { method: 'delete' }),
    },
    documents: {
      upload: (id: any, data: FormData): Promise<any> => request(`/api/v2/assets/${id}/documents`, { method: 'post', data }, { 'Content-Type': 'false' }),
      download: (id: any): Promise<Blob> => request(`/api/v2/assets/documents/${id}`, { responseType: 'blob' }),
    },
    editRequests: {
      create: (assetId: number): Promise<any> => request(`/api/v2/assets/${assetId}/edit-requests`, { method: 'post' }),
      status: (assetId: number): Promise<{ status: 'none' | 'pending' | 'approved' | 'rejected'; requestId?: number }> =>
        request(`/api/v2/assets/${assetId}/edit-request-status`),
      consume: (requestId: number): Promise<any> => request(`/api/v2/asset-edit-requests/${requestId}/consume`, { method: 'post' }),
      getDetails: (token: string): Promise<any> => request(`/api/v2/asset-edit-requests/${token}`),
      resolve: (token: string, action: 'approve' | 'reject'): Promise<{ alreadyHandled: boolean; approved: boolean }> =>
        request(`/api/v2/asset-edit-requests/${token}/${action}`, { method: 'post' }),
    },
  }

  public assetMaintenanceSchedules = {
    get: (): Promise<any> => request('/api/v2/AssetMaintenanceSchedules')
  }

  public forms = {
    dynamic: {
      template: (id: number) => request(`/api/v2/companyForms/dynamic/${id}`),
      get: (id: number): Promise<any> => request(`/api/v2/formCompletion/${id}`),
      post: (data: any): Promise<any> => request(`/api/v2/formCompletion`, { method: 'post', data }, { 'content-type': 'application/json' }),
      put: (data: any): Promise<any> => request(`/api/v2/formCompletion`, { method: 'put', data }, { 'content-type': 'application/json' }),
      attachments: {
        get: (id: number): Promise<any> => request(`/api/v2/formCompletion/${id}/attachments`),
        post: (id: number, data: FormData): Promise<any> =>
          request(
            `/api/v2/formCompletion/${id}/attachment`,
            { method: 'post', data },
            {
              'Content-Type': 'false',
            }
          ),
        delete: (id: number, attachmentId: string): Promise<any> => request(`/api/v2/formCompletion/${id}/attachment/${attachmentId}`, { method: 'delete' }),
        download: (formId: number, attachmentId: string): Promise<Blob> => request(`/api/v2/formCompletion/${formId}/attachment/${attachmentId}`, { responseType: 'blob' }),
      },
    },
    detail: {
      get: (id: number): Promise<Blob> => request(`/odata/FormCompletions(${id})/Workaware.Server.Detail()`, { responseType: 'blob' }, { Accept: 'application/pdf' }),
      post: (id: number, data: any): Promise<any> => request(`/odata/FormCompletions(${id})/Workaware.Server.PutFormDetail`, { method: 'post', data }, { 'content-type': 'application/pdf' }),
    },
    anonymous: {
      all: (): Promise<any> => request(`/api/v2/companyForms/anonymous`),
      create: (data: AnonymousFormModel): Promise<any> => request(`/api/v2/companyForms/anonymous`, { method: 'post', data }),
      delete: (id: string): Promise<any> => request(`/api/v2/companyForms/anonymous/${id.toLocaleUpperCase()}`, { method: 'delete' }),
      update: (id: string, data: AnonymousFormModel): Promise<any> => request(`/api/v2/companyForms/anonymous/${id}`, { method: 'put', data }),
    },
    templates: {
      all: (): Promise<any> => request(`/api/v2/companyForms`),
      get: (id: number): Promise<CompanyFormModel> => request(`/api/v2/companyForms/${id}`),
      update: (template: CompanyFormModel): Promise<CompanyFormModel> => request(`/api/v2/companyForm`, { method: 'put', data: template }),
      insert: (template: CompanyFormModel): Promise<CompanyFormModel> => request(`/api/v2/companyForm`, { method: 'post', data: template }),
      delete: (id: number): Promise<any> => request(`/api/v2/companyForms/${id}`, { method: 'delete' }),
      paged: (args: PagedArguments & { dynamic: boolean; showAll: boolean }): Promise<any> => {
        const { offset, take, showAll, dynamic } = args
        return request(`/api/v2/companyForms/?offset=${offset}&take=${take}&showAll=${showAll ? 1 : 0}&dynamic=${dynamic ? 1 : 0}`)
      },
      pagedFilteredTagged: (args: PagedArguments & FormTemplateFilterArguments): Promise<any> => {
        const { offset, take, ...filterArgs } = args
        return request(`/api/v2/companyForms/filtered?offset=${args.offset}&take=${args.take}`, { method: 'post', data: filterArgs })
      },
      pagedFilteredDynamicFormsTagged: (args: PagedArguments & FormTemplateFilterArguments): Promise<any> => {
        const { offset, take, ...filterArgs } = args
        return request(`/api/v2/companyforms/filteredDynamicForms?offset=${args.offset}&take=${args.take}`, { method: 'post', data: filterArgs })
      },
      search: {
        tags: (search: string = '', count: number = 5): Promise<string[]> => {
          return request(`/api/v2/companyForms/search/tags?search=${search}&count=${count}`)
        },
        notificationLevels: (search: string = '', count: number = 5): Promise<Dictionary<string>> => {
          return request(`/api/v2/companyforms/search/notificationlevels?search=${search}&count=${count}`)
        },
      },
      getAll: (): Promise<any> => request(`/odata/CompanyForms?showall=1`),
      allCompact: (): Promise<any> => request(`/odata/CompanyForms?showall=1&$select=Id,Name,Color`),
      allWithDates: (): Promise<any> => request(`/odata/CompanyForms?showall=1&$select=Id,Name,Color,DateUploaded&$orderby=DateUploaded desc&$filter=IsDeleted eq false`),
      active: (): Promise<any> => request(`/odata/CompanyForms?$filter=IsActive&$orderby=Name`),
      grouped: (): Promise<any> => request(`/odata/CompanyForms/Workaware.Server.Grouped()/?$expand=Forms,Groups($expand=Forms)`),
      //get: (id: number): Promise<any> => request(`/odata/CompanyForms(${id})`),
      createFormCompletionRecord: (id: number): Promise<any> => request(`/odata/CompanyForms(${id})/Workaware.Server.CreateFormCompletion()`),
      getBlank: (id: number): Promise<Blob> => request(`/odata/CompanyForms(${id})/Workaware.Server.GetTemplate()`, { responseType: 'blob' }),
      getPopulated: (id: number): Promise<Blob> => request(`/odata/CompanyForms(${id})/Workaware.Server.PopulatedTemplate()`, { responseType: 'blob' }),
      sendNotification: (id: number, userIds: number[], isNew: boolean): Promise<any> => request(`/odata/CompanyForms(${id})/Workaware.Server.SendNotificationForCompanyForm`, { method: 'post', data: { UserIds: userIds, IsNew: isNew } }),
      post: (data: any): Promise<any> => request(`/api/v2/companyForms/dynamic`, { method: 'post', data }, { 'content-type': 'application/json' }),
      put: (id: number | undefined, data: any): Promise<any> => request(`/api/v2/companyForms/dynamic/${id}`, { method: 'put', data }, { 'content-type': 'application/json' }),
      uploadPdf: (data: FormData): Promise<any> => request(`/api/v2/companyforms/pdf`, { method: 'post', data }),
    },
    attachments: {
      get: (id: number): Promise<any> => request(`/odata/FormCompletions(${id})/Attachments`),
      post: (id: number, data: any): Promise<any> => request(`/odata/FormCompletions(${id})/Attachments`, { method: 'post', data }),
      delete: (id: number, data: any): Promise<any> => request(`/odata/FormCompletions(${id})/Attachments`, { method: 'delete' }),
    },
    completed: {
      //query can include all odata clauses
      //queriedWithCount: (query: string): Promise<any> => request(`/api/v2/formCompletions?$count=true&${query}`),
      //queriedWithCount: (query: string): Promise<any> => request(`/odata/FormCompletions?$count=true&${query}`),
      aggregated: (data: any): Promise<any> => request(`/api/v2/formCompletions/aggregated`, { method: 'post', data }),
      all: (query?: string): Promise<any> => request(`/api/v2/formCompletions?${query || ''}`),
      paged: (args: PagedArguments, query?: string): Promise<any> => {
        // OData endpoint — supports $filter/$orderby server-side (v2 endpoint ignores them).
        // No $expand: some completions reference deleted templates; $expand causes an implicit
        // INNER JOIN that drops those rows. TemplateName comes from the base entity directly.
        const skip = args.offset;
        const top = args.take;
        const filterPart = query ? `&${query}` : '';
        return request(`/odata/FormCompletions?$count=true&$skip=${skip}&$top=${top}${filterPart}`);
      },
      total: (): Promise<any> => request(`/odata/FormCompletions/$count`),
      //filter should be $filter clause only; including $skip or $top will cause a server error
      totalFiltered: (filter: string): Promise<any> => request(`/odata/FormCompletions?$count=true&$top=0&${filter}`),
      get: (id: number): Promise<any> => request(`/api/v2/formCompletion/${id}`),
      delete: (id: number): Promise<any> => request(`/odata/FormCompletions(${id})`, { method: 'delete' }),
      changeOwner: (formId: number, newOwnerId: number): Promise<any> => request(`/odata/FormCompletions(${formId})/ChangeOwner`, { method: 'post', data: { NewOwner: newOwnerId } }),
      downloadAll: (filter: any): Promise<Blob> => request(`/api/FormCompletionAPI/DownloadCompletedFormList`, { method: 'post', data: filter, responseType: 'blob' }),
    },
    stateFlowTemplates: {
      getAll: (): Promise<any> => request(`/api/v2/stateFlowTemplates`),
    },
    associatedTeams: {
      get: (id: number): Promise<any> => request(`/api/v2/companyform/${id}/teamaccess`, { method: 'get' }),
      post: (data: any, id: number): Promise<any> => request(`/api/v2/companyform/${id}/teamaccess/`, { method: 'post', data })
    }
  }

  public teamFileLink = {
    link: (dto: TeamFileLinkDto): Promise<any> => request(`/api/v2/teamFileLink/`, { method: 'post', data: JSON.stringify(dto) }),
    unlink: (dto: TeamFileLinkDto): Promise<any> => request(`/api/v2/teamFileLink/`, { method: 'delete', data: JSON.stringify(dto) }),
  }

  public download = {
    file: (id: number): Promise<Blob> => request(`/api/DownloadAPI/GetFile/${this._getCompanyId()}/${this._getCurrentUserId()}/${id}`, { responseType: 'blob' }),
    userFile: (file: string): Promise<Blob> => request(`/api/DownloadAPI/GetUserFile/${this._getCompanyId()}/${this._getCurrentUserId()}/10/${file}/`, { responseType: 'blob' }),
    fileInCultureGroup: (id: string): Promise<any> => request(`/api/DownloadAPI/GetFileInCultureGroup/${this._getCompanyId()}/${this._getCurrentUserId()}/${id}`, { responseType: 'blob' }),
  }

  public folders = {
    getAll: (): Promise<any> => request(`/api/FileAPI/GetFolders/${this._getCompanyId()}`),
    create: (name: string): Promise<any> => request(`/api/FileAPI/CreateFolder`, { method: 'post', data: { name } }),
    delete: (folder: string): Promise<any> => request(`/api/FileAPI/DeleteFolder`, { method: 'post', data: JSON.stringify(folder) }),
    rename: (oldName: string, newName: string): Promise<void> =>
      request(`/api/FileAPI/RenameFolder`, {
        method: 'post',
        data: {
          NewFolderName: newName,
          OldFolderName: oldName,
        },
      }),
    changeFolder: (id: number, newName: string): Promise<void> =>
      request(`/api/v2/file/changeFileFolder`, {
        method: 'post',
        data: JSON.stringify({
          Id: id,
          NewFolderName: newName,
        }),
      }),
  }

  public files = {
    cultureGroups: {
      getAll: (): Promise<CultureGroupModel[]> => request(`/api/v2/cultureGroups`),
      get: (id: string): Promise<CultureGroupModel | undefined> => request(`/api/v2/cultureGroup/${id}`),
      create: (data: CultureGroupModel): Promise<CultureGroupModel | undefined> => request(`/api/v2/cultureGroup`, { method: 'post', data }),
      update: (data: CultureGroupModel): Promise<CultureGroupModel | undefined> => request(`/api/v2/cultureGroup`, { method: 'put', data }),
      delete: (id: string): Promise<any> => request(`/api/v2/cultureGroup/${id}`, { method: 'delete' }),
      link: (fileId: number, groupId: string): Promise<CultureGroupModel> => request(`/api/v2/file/cultureGroup/${fileId}/${groupId}`, { method: 'post' }),
      unlink: (fileId: number): Promise<void> => request(`/api/v2/file/cultureGroup/${fileId}`, { method: 'delete' }),
    },
    getAll: (): Promise<any> => request(`/api/v2/files?includeBlankFilenames`),
    get: (id: number): Promise<any> => request(`/api/v2/file/${id}`),
    create: (data: any): Promise<any> => request(`/odata/Files`, { method: 'post', data }),
    put: (id: number, data: any): Promise<any> => request(`/odata/Files(${id})`, { method: 'put', data }),
    update: (id: number, data: any): Promise<any> => request(`/odata/Files(${id})`, { method: 'patch', data }),
    delete: (id: number): Promise<any> => request(`/odata/Files(${id})`, { method: 'delete' }),
    upload: (data: any, filename: string): Promise<any> =>
      request(
        `/api/UploadAPI`,
        { method: 'post', data },
        {
          'Content-Type': 'false',
          UploadType: UploadTypes.CompanyFiles.value.toString(),
          FileName: filename.replace(/[^\x00-\xFF]/g, encodeURIComponent),
        }
      ),
  }

  public company = {
    get: (id: number): Promise<any> => request(`/api/v2/company/${id}`),
    getLogo: (fileName: string): Promise<Blob> => request(`/api/DownloadAPI/GetServerFile/${this._getCompanyId()}/${this._getCurrentUserId()}/15/${fileName}/?ZUMO-API-VERSION=2.0.0`, { responseType: 'blob' }),
    update: (id: number, data: any): Promise<any> => request(`/odata/Companies(${id})`, { method: 'patch', data }),
    uploadLogo: (data: any, filename: string): Promise<any> =>
      request(
        `/api/UploadAPI`,
        { method: 'post', data },
        {
          'Content-Type': 'false',
          UploadType: UploadTypes.CompanyFileLogos.value.toString(),
          FileName: filename,
        }
      ),
  }

  public picklistItems = {
    getType: (id: number): Promise<any> => request(`/api/v2/picklist/${id}`),
    get: (id: number): Promise<any> => request(`/odata/PicklistItems(${id})`),
    create: (data: any): Promise<any> => request(`/odata/PicklistItems`, { method: 'post', data }),
    put: (id: number, data: any): Promise<any> => request(`/odata/PicklistItems(${id})`, { method: 'put', data }),
    update: (id: number, data: any): Promise<any> => request(`/odata/PicklistItems(${id})`, { method: 'patch', data }),
    delete: (id: number): Promise<any> => request(`/odata/PicklistItems(${id})`, { method: 'delete' }),
    all: (): Promise<any> => request(`/odata/PicklistItems`),
    getByType: (type: number): Promise<any> => request(`/odata/PicklistItems?$filter=Type eq ${type} and IsActive eq true&$orderby=SortOrder asc`),
    reset: (typeId: number): Promise<any> => request(`/api/PicklistAPI/Reset/${typeId}`),
  }
  public emailTemplate = {
    getAll: (): Promise<{ value: string; name: string }[]> => request(`/api/v2/GetEmailTemplateTypes`),
    create: (data: any): Promise<any> => request(`/api/v2/EmailTemplatePost`, { method: 'post', data }),
    getTemplate: (emailType: number): Promise<any> => request(`/api/v2/GetDefaultEmailTemplate/${emailType}`),
    getByTypeId: (typeId: number): Promise<EmailTemplateModel[]> => request(`/api/v2/GetByTypeId/${typeId}`),
    delete: (id: number): Promise<any> => request(`/odata/EmailTemplates(${id})`, { method: 'delete' }),
    put: (data: any): Promise<any> => request(`/api/v2/EmailTemplate`, { method: 'put', data }),
  }
  public picklistTypes = {
    getAll: (): Promise<{ Key: number; Value: string }[]> => request(`/api/v2/picklists`),
    getAssetTypes: (): Promise<{ name: string; value: string }[]> => request(`/api/v2/AssetPicklistTypes`),
    get: (id: number): Promise<any> => request(`/odata/CompanyPicklistTypes(${id})`),
    create: (data: any): Promise<any> => request(`/odata/CompanyPicklistTypes`, { method: 'post', data }),
    put: (id: number, data: any): Promise<any> => request(`/odata/CompanyPicklistTypes(${id})`, { method: 'put', data }),
    update: (id: number, data: any): Promise<any> => request(`/odata/CompanyPicklistTypes(${id})`, { method: 'patch', data }),
    delete: (id: number): Promise<any> => request(`/odata/CompanyPicklistTypes(${id})`, { method: 'delete' }),
  }

  public user = {
    all: (): Promise<any> => request(`/odata/Users`),
    allMinimal: (): Promise<any> => request(`/odata/Users?$select=Id,Firstname,Lastname,Username,PhotoURL`),
    byIds: (ids: number[]): Promise<any> => {
      if (ids.length === 0) return Promise.resolve({ value: [] })
      const filter = ids.map(id => `Id eq ${id}`).join(' or ')
      return request(`/odata/Users?$filter=${filter}&$select=Id,Firstname,Lastname,Username`, {}, { 'X-WORKAWARE-TEAMFILTER': 'false' })
    },
    paged: (args: PagedArguments): Promise<PagedResult<UserModel>> => {
      let url = `/api/v2/users?offset=${args.offset}&take=${args.take}`
      if (args.filter?.name) {
        url += `&nameFilter=${args.filter.name}`
      }
      return request(url)
    },
    inActivePaged: (args: PagedArguments): Promise<PagedResult<UserModel>> => {
      let url = `/api/v2/inActiveUsers?offset=${args.offset}&take=${args.take}`
      if (args.filter?.name) {
        url += `&nameFilter=${args.filter.name}`
      }
      return request(url)
    },

    active: (): Promise<any> => request(`/odata/Users?$filter=IsActive&$orderby=Lastname asc,Firstname asc,PreferredName asc`),
    activePhotoMap: (): Promise<any> => request(`/odata/Users?$filter=IsActive&$select=Id,PhotoURL`),
    activeNameMap: (): Promise<Record<number, string>> =>
      request(`/odata/Users?$filter=IsActive&$select=Id,Firstname,Lastname,PreferredName,Email`)
        .then((r: any) => {
          const map: Record<number, string> = {}
          ;(r?.value ?? r ?? []).forEach((u: any) => {
            const id = Number(u?.Id ?? u?.id)
            if (!isNaN(id)) {
              const name =
                [u?.PreferredName || u?.Firstname, u?.Lastname].filter(Boolean).join(' ') ||
                u?.Email || u?.email || ''
              if (name) map[id] = name
            }
          })
          return map
        }),
    // userId -> RoleName map (joins active users with the roles list since the
    // training-summary endpoint doesn't carry role information)
    activeRoleMap: (): Promise<Record<number, string>> =>
      Promise.all([
        request(`/odata/Users?$filter=IsActive&$select=Id,RoleId`),
        request(`/api/rolesAPI/roles`),
      ]).then(([usersResp, rolesResp]: any[]) => {
        const roleNameById: Record<number, string> = {}
        ;(rolesResp?.value ?? rolesResp ?? []).forEach((r: any) => {
          const rid = Number(r?.Id ?? r?.id)
          if (!isNaN(rid)) roleNameById[rid] = r?.RoleName ?? r?.roleName ?? ''
        })
        const map: Record<number, string> = {}
        ;(usersResp?.value ?? usersResp ?? []).forEach((u: any) => {
          const id = Number(u?.Id ?? u?.id)
          const rid = Number(u?.RoleId ?? u?.roleId)
          if (!isNaN(id) && !isNaN(rid) && roleNameById[rid]) map[id] = roleNameById[rid]
        })
        return map
      }),
    get: (id: number): Promise<any> => request(`/odata/Users(${id})`),
    getDbId: (id: number): Promise<any> => request(`/odata/Users(${id})/GetDbId`, { method: 'post' }),
    create: (data: UserModel): Promise<any> => request(`/odata/Users`, { method: 'post', data }),
    put: (id: number, data: any): Promise<any> => request(`/odata/Users(${id})`, { method: 'put', data }),
    updatePostPickList: (id: number): Promise<any> => request(`/odata/Users(${id})/UpdatePostPickList`, { method: 'post', silent: true }),
    updatePutPickList: (id: number): Promise<any> => request(`/odata/Users(${id})/UpdatePutPickList`, { method: 'post', silent: true }),
    update: (id: number, data: Dictionary<any>): Promise<any> => request(`/odata/Users(${id})`, { method: 'patch', data, silent: true }),
    delete: (id: number): Promise<any> => request(`/odata/Users(${id})`, { method: 'delete' }),
    updateDeletePickList: (id: number): Promise<any> => request(`/odata/Users(${id})/UpdateDeletePickList`, { method: 'post', silent: true }),
    uploadPhoto: (data: any, filename: string): Promise<any> =>
      request(
        `/api/UploadAPI`,
        { method: 'post', data },
        {
          'Content-Type': 'false',
          UploadType: UploadTypes.UserFilePhotos.value.toString(),
          FileName: filename,
        }
      ),
    photo: (companyId: number, userId: number, url: string): Promise<any> => request(`/api/DownloadAPI/GetUserFile/${companyId}/${userId}/10/${url}/?ZUMO-API-VERSION=2.0.0`),
    signature: (companyId: number, userId: number, url: string): Promise<any> => request(`/api/DownloadAPI/GetUserFile/${companyId}/${userId}/14/${url}/?ZUMO-API-VERSION=2.0.0`),
    generatePassword: (data: PasswordGenerationRequest): Promise<any> => request(`/api/userApi/generateAndSetPassword`, { method: 'patch', data }),
    updatePassword: (data: PasswordUpdateRequest): Promise<any> => request(`/api/userApi/updatePassword`, { method: 'post', data }),
    changePassword: (data: PasswordChangeRequest): Promise<any> => request(`/api/userApi/changePassword`, { method: 'post', data }),
    getNotificationPreferences: (): Promise<any> => request(`/api/userApi/notificationPreferences`),
    saveNotificationPreferences: (data: any): Promise<any> => request(`/api/userApi/notificationPreferences`, { method: 'post', data }),
    getSystemPreferences: (): Promise<any> => request(`/api/userApi/systemPreferences`),
    saveSystemPreferences: (data: any): Promise<any> => request(`/api/userApi/systemPreferences`, { method: 'post', data }),
    checkUsername: (Username: string): Promise<UsernameExistsDto> => request(`/api/v2/users/checkUsername`, { method: 'post', data: JSON.stringify(Username) }),
    checkPersonalEmail: (PersonalEmail: string): Promise<UsernameExistsDto> => request(`/api/v2/users/checkEmail`, { method: 'post', data: JSON.stringify(PersonalEmail) }),
    checkWorkEmail: (WorkEmail: string): Promise<UsernameExistsDto> => request(`/api/v2/users/checkEmail`, { method: 'post', data: JSON.stringify(WorkEmail) }),
    batch: (ids: number[]): Promise<any[]> => request(`/api/v2/users/batch`, { method: 'post', data: { userIds: ids } }),
    getTwoFactorSettings: (): Promise<any> => request(`/api/userApi/twoFactorSettings`),
    updateTwoFactorSettings: (data: any): Promise<any> => request(`/api/userApi/twoFactorSettings`, { method: 'post', data }),
    getSessionTimeoutSettings: (): Promise<any> => request(`/api/userApi/sessionTimeoutSettings`),
    saveSessionTimeoutSettings: (data: any): Promise<any> => request(`/api/userApi/sessionTimeoutSettings`, { method: 'post', data }),
  }

  public roles = {
    list: (): Promise<any[]> => request(`/api/rolesAPI/roles`),
    create: (data: any): Promise<any> => request(`/api/rolesAPI/roles`, { method: 'post', data }),
    update: (id: number, data: any): Promise<any> => request(`/api/rolesAPI/roles/${id}`, { method: 'put', data }),
    delete: (id: number, data?: any): Promise<any> => request(`/api/rolesAPI/roles/${id}`, { method: 'delete', data: data || {} }),
    getModuleAccess: (id: number): Promise<any[]> => request(`/api/rolesAPI/roles/${id}/moduleAccess`),
    updateModuleAccess: (id: number, data: any): Promise<any[]> => request(`/api/rolesAPI/roles/${id}/moduleAccess`, { method: 'put', data }),
    getUsersByNotificationLevel: (level: number): Promise<UserModel[]> => request(`/api/rolesAPI/UsersByNotificationLevel/${level}`),
  }

  public teams = {
    //getByIds: (ids: number[]): Promise<any> => request(`/odata/OrganizationTeamHierarchies?${this._buildIdMatchingFilter(ids)}`),
    getByIds: (ids: number[]): Promise<any> => request(`/api/v2/teams/?${ids.map((id) => 'ids=' + id).join('&')}`),
    all: (): Promise<any> => request(`/api/v2/teams`),
    mine: (): Promise<TeamModel[]> => request(`/api/v2/teams/mine`),
    get: (id: number): Promise<any> => request(`/api/v2/team/${id}`),
    patch: (id: number, data: any): Promise<any> => request(`/odata/OrganizationTeamHierarchies(${id})`, { method: 'patch', data }),
    post: (data: any): Promise<any> => request(`/api/v2/teams`, { method: 'post', data }),
    delete: (id: number): Promise<any> => request(`/odata/OrganizationTeamHierarchies(${id})`, { method: 'delete' }),
    members: {
      all: (): Promise<any> => request(`/odata/OrganizationTeamMemberHierarchies`),
      get: (id: number): Promise<any> => request(`/odata/OrganizationTeamMemberHierarchies?$filter=OrganizationTeamHierarchyId eq ${id}`),
      delete: (id: number): Promise<any> => request(`/odata/OrganizationTeamMemberHierarchies(${id})`, { method: 'delete' }),
      post: (data: any): Promise<any> => request(`/odata/OrganizationTeamMemberHierarchies`, { method: 'post', data }),
      postV2: (data: any, id: number): Promise<any> => request(`/api/v2/team/${id}/UpdateMembers`, { method: 'post', data })
    },
    attributes: {
      all: (id: number): Promise<any> => request(`/odata/OrganizationTeamHierarchyAttributes?$filter=OrganizationTeamHierarchyId%20eq%20${id}`),
      getCalculated: (id: number): Promise<any> => request(`/odata/OrganizationTeamHierarchies(${id})/Workaware.Server.GetEffectiveAttributes()/`),
      create: (data: any): Promise<any> => request(`/odata/OrganizationTeamHierarchyAttributes`, { method: 'post', data }),
      update: (id: number, data: any): Promise<any> => request(`/odata/OrganizationTeamHierarchyAttributes(${id})`, { method: 'patch', data }),
      remove: (id: number): Promise<any> => request(`/odata/OrganizationTeamHierarchyAttributes(${id})`, { method: 'delete' }),
    },
    associatedForms: {
      get: (id: number): Promise<any> => request(`/api/v2/team/${id}/formaccess`, { method: 'get' }),
      post: (data: any, id: number): Promise<any> => request(`/api/v2/team/${id}/formaccess`, { method: 'post', data })
    }
  }

  public messaging = {
    serviceConnectionString: () => request(`/api/Messaging/ConnectionString`),
  }
  private urlSafeBase64Encode = (string: string) => {
    //remove trailing =
    var base64 = btoa(string)
    while (base64[base64.length - 1] === '=') {
      base64 = base64.slice(0, base64.length - 1)
    }
    return base64
      .replace(/\+/g, '.')
      .replace(/\//g, '_')
      .replace(/=/g, '-')
  }
  public assignment = {
    assignedToLoggedInUser: {
      all: (): Promise<{ value: any }> => request(`/odata/Assignments/Workaware.Server.AllAssignedToDirectly()/`),
      incomplete: (): Promise<{ value: any }> => request(`/odata/Assignments/Workaware.Server.AllAssignedToDirectly()/?$filter=CompletedOn eq null or CompletedByUserId eq null&$orderby=AssignedOn`),
    },
    assignedByLoggedInUser: {
      incompleteTopLevel: (): Promise<{ value: any }> => request(`/odata/Assignments/Workaware.Server.AllAssignedBy()/?$filter=(CompletedOn eq null or CompletedByUserId eq null) and ParentAssignmentId eq null&$orderby=AssignedOn desc`),
      incomplete: (): Promise<{ value: any }> => request(`/odata/Assignments/Workaware.Server.AllAssignedBy()/?$filter=CompletedOn eq null or CompletedByUserId 0eq null&$orderby=AssignedOn desc`),
      all: (): Promise<{ value: any }> => request(`/odata/Assignments/Workaware.Server.AllAssignedBy()/`),
    },
    v2: {
      recurringAssignment: {
        all: (): Promise<RecurringAssignmentDto[]> => request(`/api/v2/recurringAssignment/getAll`),
        upSert: (Id: number | undefined, data: RecurringAssignmentDto): Promise<RecurringAssignmentDto[]> => request(`/api/v2/recurringAssignment/${Id}`, { method: 'POST', data }),
        delete: (Id: number): Promise<RecurringAssignmentDto[]> => request(`/api/v2/recurringAssignment/delete/${Id}`, { method: 'post' })
      },
      assignmentStatus: {
        get: (): Promise<AssignmentStatusDto[]> => request(`/api/v2/assignmentstatuses`),
        getActiveStatuses: (): Promise<AssignmentStatusDto[]> => request(`/api/v2/assignmentstatuses/active`),
        post: (data: AssignmentStatusDto): Promise<any> => request(`/api/v2/assignmentstatuses`, { method: 'POST', data }),
        delete: (Id: string): Promise<any> => request(`/api/v2/assignmentstatuses/${Id}`, { method: 'post' })
      },
      get: (id: number) => request(`/api/v2/assignmentDetails?$filter=Id eq ${id}`),
      getMany: (ids: number[]) => request(`/api/v2/assignmentDetails?$filter=${ids.map(id => `Id eq ${id}`).join(' or ')}`),
      getManyUsers: (ids: number[]) => request(`/api/v2/assignmentdetails/users?$filter=${ids.map(id => `Id eq ${id}`).join(' or ')}`),
      update: (id: number, data: any) => request(`/api/v2/updateAssignment/${id}`, { method: 'post', data }),
      getNotificationUsers: (assignmentId: number) => request(`/api/v2/AssignmentNotificationUsers/${assignmentId}`),
      saveNotificationUsers: (assignmentId: number, data: { notificationLevel: number, userIds: number[] }) =>
        request(`/api/v2/AssignmentNotificationUsers/${assignmentId}`, { method: 'post', data }),
      assignedToLoggedInUser: {
        all: () => request(`/api/v2/AllAssignedToDirectly`),
        incomplete: () => request(`/api/v2/AllAssignedToDirectly?incompleteOnly=true`),
      },
      assignedByLoggedInUser: {
        all: () => request(`/api/v2/AllAssignedBy`),
        incompleteTopLevel: () => request(`/api/v2/AllAssignedBy?incompleteOnly=true&topLevelOnly=true`),
        get: (id: number) => request(`/api/v2/AllAssignedBy?id=${id}`),
      },
      allOverdueForCompany: (filter?: { dateFrom?: string; dateTo?: string; assignedToUserId?: number }) => {
        const p = new URLSearchParams()
        if (filter?.dateFrom) p.set('dateFrom', filter.dateFrom)
        if (filter?.dateTo) p.set('dateTo', filter.dateTo)
        if (filter?.assignedToUserId) p.set('assignedToUserId', String(filter.assignedToUserId))
        const qs = p.toString()
        return request(`/api/v2/AllOverdueForCompany${qs ? '?' + qs : ''}`)
      },
      allForCompany: (status?: 'open' | 'completed', filter?: { dateFrom?: string; dateTo?: string; assignedToUserId?: number }) => {
        const p = new URLSearchParams()
        if (status) p.set('status', status)
        if (filter?.dateFrom) p.set('dateFrom', filter.dateFrom)
        if (filter?.dateTo) p.set('dateTo', filter.dateTo)
        if (filter?.assignedToUserId) p.set('assignedToUserId', String(filter.assignedToUserId))
        const qs = p.toString()
        return request(`/api/v2/AllForCompany${qs ? '?' + qs : ''}`)
      },
      /**
       * Sets the assignment as completed by the currently authenticated user
       * @param id
       */
      complete: (id: number) => request(`/api/v2/completeassignment/${id}`),
      completeAssignmentInBulk: (data: AssignmentDto[]) => request(`/api/v2/CompleteAssignmentInBulk/`, { method: 'post', data }),
    },
    GetAssignmentsOnFormCompletion: (data: any) => request(`/odata/Assignments/Workaware.Server.GetAssignmentsOnFormCompletion()/`, { method: 'post', data }),

    all: () => request(`/odata/Assignments`),
    incompleteCount: () => request(`/odata/Assignments?$filter=ParentAssignmentId eq null and (CompletedOn eq null or CompletedByUserId eq null)&$top=0&$count=true`),
    get: (id: number) => request(`/odata/Assignments(${id})`),
    allTopLevel: (): Promise<any> => request(`/odata/Assignments?$filter=ParentAssignmentId eq null`),
    incompleteTopLevel: (limit: number = 10): Promise<any> => request(`/odata/Assignments?$filter=ParentAssignmentId eq null and (CompletedOn eq null or CompletedByUserId eq null)&$orderby=AssignedOn&$top=${limit}`),
    item: (id: number): Promise<any> => request(`/odata/Assignments(${id})/Workaware.Server.GetAssignedItem()/?$expand=FormCompletion,Template`),
    update: (id: number, data: any): Promise<any> => request(`/odata/Assignments(${id})`, { method: 'patch', data }),
    delete: (id: number): Promise<any> => request(`/odata/Assignments(${id})`, { method: 'delete' }),
    sendNotification: (id: number, removedUserIds?: number[], isDueDateChange?: boolean): Promise<any> => request(`/odata/Assignments(${id})/Workaware.Server.SendNotificationForAssignment`, { method: 'post', data: { RemovedUserIds: removedUserIds ?? [], IsDueDateChange: isDueDateChange ?? false } }),
    children: (id: string): Promise<{ value: any }> => request(`/odata/Assignments?$filter=ParentAssignmentId eq ${id}`),
    incompleteChildren: (id: string): Promise<{ value: any }> => request(`/odata/Assignments?$filter=ParentAssignmentId eq ${id} and (CompletedOn eq null or CompletedByUserId eq null)`),
    create: {
      fromTemplate: (id: number): Promise<any> => request(`/odata/CompanyForms(${id})/Workaware.Server.CreateAssignment()/`),
      fromForm: (id: number): Promise<any> => request(`/odata/FormCompletions(${id})/Workaware.Server.CreateAssignment()/`),
      fromModel: (data: any) => request(`/api/v2/createAssignment`, { method: 'post', data }),
    },
    forEvent: (id: string) => request(`/api/v2/assignmentsForEvent/${this.urlSafeBase64Encode(id)}`),
  }
  public scheduledItemSettings = {
    eventTypes: {
      get: (): Promise<ScheduledEventTypeDto[]> => request(`/api/v2/scheduledeventtypes`),
      getActive: (): Promise<ScheduledEventTypeDto[]> => request(`/api/v2/scheduledeventtypes/active`),
      post: (data: ScheduledEventTypeDto): Promise<ScheduledEventTypeDto[]> => request(`/api/v2/scheduledeventtypes`, { method: 'POST', data }),
      delete: (id: string): Promise<ScheduledEventTypeDto[]> => request(`/api/v2/scheduledeventtypes/${id}`, { method: 'post' }),
    },
    statusTypes: {
      get: (): Promise<ScheduledStatusTypeDto[]> => request(`/api/v2/scheduledstatustypes`),
      getActive: (): Promise<ScheduledStatusTypeDto[]> => request(`/api/v2/scheduledstatustypes/active`),
      post: (data: ScheduledStatusTypeDto): Promise<ScheduledStatusTypeDto[]> => request(`/api/v2/scheduledstatustypes`, { method: 'POST', data }),
      delete: (id: string): Promise<ScheduledStatusTypeDto[]> => request(`/api/v2/scheduledstatustypes/${id}`, { method: 'post' }),
    },
    visibilityTypes: {
      get: (): Promise<ScheduledVisibilityTypeDto[]> => request(`/api/v2/scheduledvisibilitytypes`),
      getActive: (): Promise<ScheduledVisibilityTypeDto[]> => request(`/api/v2/scheduledvisibilitytypes/active`),
      post: (data: ScheduledVisibilityTypeDto): Promise<ScheduledVisibilityTypeDto[]> => request(`/api/v2/scheduledvisibilitytypes`, { method: 'POST', data }),
      delete: (id: string): Promise<ScheduledVisibilityTypeDto[]> => request(`/api/v2/scheduledvisibilitytypes/${id}`, { method: 'post' }),
    },
  }
  public companyModuleSecurity = {
    all: (): Promise<any> => request(`/odata/CompanyModuleSecurity`),
    update: (id: number, data: any): Promise<any> => request(`/odata/CompanyModuleSecurity(${id})`, { method: 'patch', data }),
  }
  public journeyManagement = {
    getAllJourneys: (): Promise<any> => request('/odata/Journeys'),
    create: (data: JourneyModel): Promise<any> => request('/odata/Journeys', { method: 'post', data }),
    update: (id: number, data: JourneyModel): Promise<any> => request(`/odata/Journeys(${id})`, { method: 'put', data }),
    delete: (id: number): Promise<any> => request(`/odata/Journeys(${id})`, { method: 'delete' }),
    get: (id: number): Promise<any> =>request(`/odata/Journeys(${id})?$expand=JourneyCheckpoints($expand=UserStatuses),JourneyUsers`),
    getList: (filter?: { dateFrom?: string; dateTo?: string }): Promise<any> => {
      const params = new URLSearchParams()
      if (filter?.dateFrom) params.append('dateFrom', filter.dateFrom)
      if (filter?.dateTo) params.append('dateTo', filter.dateTo)
      const qs = params.toString()
      return request(qs ? `/api/v2/journey?${qs}` : '/api/v2/journey')
    },
    getAllWithCheckpoints: (): Promise<any[]> => request('/odata/Journeys?$expand=JourneyCheckpoints($expand=UserStatuses),JourneyUsers').then((r: any) => r?.value ?? r),
    // Server-side paging for the Journey Management page. Same $expand as getAllWithCheckpoints, but only
    // for the requested page. `query` holds extra OData clauses ($filter/$orderby).
    getPaged: (skip: number, top?: number, query?: string): Promise<any> =>
      request(`/odata/Journeys?$count=true&$skip=${skip}${top === undefined ? '' : `&$top=${top}`}&$expand=JourneyCheckpoints($expand=UserStatuses),JourneyUsers${query ? `&${query}` : ''}`),
    // Used for the duplicate-name check: returns at most one matching journey (access rules still apply).
    findByName: (query: string): Promise<any> => request(`/odata/Journeys?$top=1&$select=Id,Name&${query}`),
    sendNotification: (id: number, newUserIds?: number[]): Promise<any> => request(`/odata/Journeys(${id})/Workaware.Server.SendNotificationForJourney`, { method: 'post', data: { NewUserIds: newUserIds ?? [] } }),
  }

  public journeyUsers = {
  delete: (id: number): Promise<any> =>
    request(`/odata/JourneyUsers(${id})`, { method: 'delete' }),
};
  public raster = {
    all: (): Promise<any> => request(`/odata/Rasters`),
    get: (id: number) => request(`/odata/Rasters(${id})`),
    create: (data: RasterModel): Promise<any> => request(`/odata/Rasters`, { method: 'post', data }),
    put: (id: number, data: any): Promise<any> => request(`/odata/Rasters(${id})`, { method: 'put', data }),
    update: (id: number, data: RasterModel): Promise<any> => request(`/odata/Rasters(${id})`, { method: 'patch', data }),
    delete: (id: any): Promise<any> => request(`/odata/Rasters(${id})`, { method: 'delete' }),
  }
  public maintenanceSchedules = {
    getList: (): Promise<any> => request(`/api/v2/MaintenanceSchedules`),
    // Server-side paging for the Maintenance Schedules page. `query` holds extra OData clauses ($filter/$orderby).
    getPaged: (skip: number, top?: number, query?: string): Promise<any> =>
      request(`/odata/CMMSMaintenanceSchedules?$count=true&$skip=${skip}${top === undefined ? '' : `&$top=${top}`}&$select=Id,Name,Description${query ? `&${query}` : ''}`),
    get: (id: number) => request(`/api/v2/MaintenanceSchedules/${id}`),
    post: (data: any): Promise<any> => request(`/api/v2/MaintenanceSchedules`, { method: 'post', data }, { 'content-type': 'application/json' }),
    put: (data: any): Promise<any> => request(`/api/v2/MaintenanceSchedules`, { method: 'put', data }, { 'content-type': 'application/json' }),
    delete: (id: any): Promise<any> => request(`/api/v2/MaintenanceSchedules/${id}`, { method: 'delete' }),
  }
  public shapeFiles = {
    all: (): Promise<any> => request('/odata/Shapefiles'),
    add: (data: ShapeFileModel): Promise<any> => request(`/odata/Shapefiles`, { method: 'post', data }),
    delete: (id: number): Promise<any> => request(`/odata/Shapefiles(${id})`, { method: 'delete' }),
    update: (id: number, data: ShapeFileModel): Promise<any> => request(`/odata/Shapefiles(${id})`, { method: 'patch', data }),
    downloadZip: (id: number): Promise<Blob> => request(`/api/DownloadAPI/GetShapefileZip/${this._getCompanyId()}/${this._getCurrentUserId()}/${id}`,{ responseType: 'blob' }),
    downloadFile: (id: number, extension: string): Promise<Blob> => request( `/api/DownloadAPI/GetShapefile/${this._getCompanyId()}/${this._getCurrentUserId()}/${extension}/${id}`,{ responseType: 'blob' } ),
  }
  public trackingCurrentPosition = {
    getTrackingUser: (): Promise<any> => request('/api/TrackingAPI/GetCurrentLocations'),
    getHistoricPositions: (data): Promise<any> => request(`/api/TrackingAPI/GetHistoricLocations`, { method: 'post', data }),
    getCompletedCheckpointHistory: (data): Promise<any> => request(`/api/v2/journeys/checkpointHistory`, { method: 'post', data }),
    getInProgressCheckpointProgress: (data): Promise<any> => request(`/api/v2/journeys/checkpointProgress`, { method: 'post', data }),
    getUsersWithCheckpointHistory: (): Promise<number[]> => request(`/api/v2/journeys/usersWithCheckpointHistory`),
    getCheckpointUserProgress: (data): Promise<any> => request(`/api/v2/journeys/checkpointUserProgress`, { method: 'post', data }),
  }
  public userShapes = {
    getUserShapes: (): Promise<any> => request('/api/UserShapes/Shapes'),
  }
  public userGeneratedShapes = {
    paged: (): Promise<any> => request(`/api/UserShapes/Shapes/`),
    post: (data: any): Promise<any> => request(`/api/UserShapes/Shapes`, { method: 'post', data }),
    delete: (id: string): Promise<any> => request(`/api/UserShapes/Shapes/${id}`, { method: 'delete' }),
    update: (id: string, data: RasterModel): Promise<any> => request(`/api/UserShapes/Shapes/${id}`, { method: 'put', data }),
  }
  public journeyCheckPoints = {
    getAll: (): Promise<any> => request('/odata/JourneyCheckpoints'),
    post: (data: any): Promise<any> => request(`/odata/JourneyCheckpoints`, { method: 'post', data }),
    update: (id: number, data: JourneyCheckPointModel): Promise<any> => request(`/odata/JourneyCheckpoints(${id})`, { method: 'patch', data }),
    delete: (id: number): Promise<any> => request(`/odata/JourneyCheckpoints(${id})`, { method: 'delete' }),
    setStatus: (id: number, data: { StatusId: number; Comment: string; TargetUserId?: number }): Promise<any> =>
      request(`/odata/JourneyCheckpoints(${id})/Workaware.Server.SetCheckpointStatus`, { method: 'post', data }),

  }
  public modulesAccess = {
    get: (): Promise<CompanyModuleAccessDto[]> => request(`/api/v2/moduleAccess/user`),
  }
  public avatar = {
    get: (relativeUrl: string): Promise<Blob> => request(relativeUrl, { responseType: 'blob' }),
  }
  public companyMessage = {
    getList: (): Promise<CompanyMessageModel[]> => request(`/api/v2/companymessages`),
    getCurrentForUser: (): Promise<CompanyMessageModel> => request(`/api/v2/companymessages/currentforuser`),
    post: (data: CompanyMessageModel): Promise<any> => request(`/api/v2/companyMessages`, { method: 'post', data }, { 'content-type': 'application/json' }),
    put: (data: CompanyMessageModel): Promise<any> => request(`/api/v2/companyMessages`, { method: 'put', data }, { 'content-type': 'application/json' }),
    delete: (id: number): Promise<any> => request(`/api/v2/companymessage/${id}`, { method: 'delete' }),
    acknowledge: (): Promise<any> => request(`/api/v2/companymessages/acknowledge`, { method: 'post' })
  }
  public courses = {
    get: (): Promise<any> => request('/odata/Courses?$orderby=Name'),
    post: (data: CourseModel): Promise<CourseModel> => request(`/odata/Courses`, { method: 'post', data }),
    put: (key: number, data: CourseModel): Promise<any> => request(`/odata/Courses(${key})`, { method: 'patch', data }),
    delete: (key: number): Promise<any> => request(`/odata/Courses(${key})`, { method: 'delete' }),
    reminder: (): Promise<any> => request(`/api/TrainingAPI/ProcessCompanyCourseReminders`)
  }
  public lmsCourses = {
    linkCertification: (id: number, courseId: number | null): Promise<any> =>
      request(`/odata/LMSCourses(${id})`, { method: 'patch', data: { CourseId: courseId } }),
    getLink: (lmsCourseId: number, userId: number): Promise<string> =>
      request(`/api/v2/training/courses/link`, { method: 'post', data: { LMSCourseId: lmsCourseId, UserId: userId } }),
  }
  public securityItems = {
    getForCourse: (courseId: number): Promise<any> => request(`/odata/SecurityItems?$filter=CourseId eq ${courseId} and Type eq 0`),
    post: (data: any): Promise<any> => request(`/odata/SecurityItems`, { method: 'post', data }),
    delete: (key: number): Promise<any> => request(`/odata/SecurityItems(${key})`, { method: 'delete' }),
  }
   public userExemptedCertificates = {
    get: (): Promise<any> => request('/odata/UserExemptedCertificates'),
    getByUser: (userId: number): Promise<any> => request(`/odata/UserExemptedCertificates?$filter=UserId eq ${userId} and IsDeleted eq false`),
    post: (data: any): Promise<any> => request(`/odata/UserExemptedCertificates`, { method: 'post', data }),
    delete: (key: number): Promise<any> => request(`/odata/UserExemptedCertificates(${key})`, { method: 'DELETE' }),
    softDelete: (key: number): Promise<any> => request(`/odata/UserExemptedCertificates(${key})`, { method: 'patch', data: { IsDeleted: true } }),
  }
  public userCompletedCertificates = {
    get: (): Promise<any> => request('/odata/UserCompletedCertificates'),
    getByUser: (userId: number): Promise<any> => request(`/odata/UserCompletedCertificates?$filter=UserId eq ${userId} and IsDeleted eq false`),
    post: (data: any): Promise<any> => request('/odata/UserCompletedCertificates', { method: 'post', data }),
    patch: (id: number, data: any): Promise<any> => request(`/odata/UserCompletedCertificates(${id})`, { method: 'patch', data }),
    delete: (id: number): Promise<any> => request(`/odata/UserCompletedCertificates(${id})`, { method: 'delete' }),
    softDelete: (id: number): Promise<any> => request(`/odata/UserCompletedCertificates(${id})`, { method: 'patch', data: { IsDeleted: true } }),
  }
  public emailLog = {
    get: (skip: number, top: number): Promise<any> => request(`/odata/EmailLogs?$count=true&$skip=${skip}&$top=${top}&$orderby=SentOn desc`),
    resendEmail: (emailLogId: number): Promise<any> => request(`/api/EmailAPI/ResendEmail/${emailLogId}`),
    delete: (key: number): Promise<any> => request(`/odata/EmailLogs(${key})`, { method: 'delete' })
  }
  public events = {
    get: (offset: number, take: number): Promise<any> => request(`/api/v2/events?offset=${offset}&take=${take}`, { silent: true }),
    delete: (key: number): Promise<any> => request(`/api/v2/events/${key}`, { method: 'delete' })
  }
  public cameraSettings = {
    get: (): Promise<any> => request(`/api/v2/camera`),
    post: (data: CameraDto): Promise<CameraDto> => request(`/api/v2/camera`, { method: 'post', data }),
    put: (data: CameraDto): Promise<any> => request(`/api/v2/camera`, { method: 'put', data }),
    delete: (key: number): Promise<any> => request(`/api/v2/camera/${key}`, { method: 'delete' }),
  }
  public smsLog = {
    get: (offset: number, take: number): Promise<any> => request(`/api/v2/smslogs?offset=${offset}&take=${take}`),
    delete: (key: number): Promise<any> => request(`/api/v2/smslogs/${key}`, { method: 'delete' })
  }
  public counters = {
    get: (): Promise<any> => request('/odata/Counters'),
    create: (data: any): Promise<any> => request('/odata/Counters', { method: 'post', data }),
    update: (key: number, data: CounterModel): Promise<any> => request(`/odata/Counters(${key})`, { method: 'patch', data }),
  }
  public tracker = {
    // `filter` is an OData filter expression (e.g. "contains(tolower(Name),'foo')").
    // Passed through encodeURIComponent so embedded single quotes / spaces survive
    // the URL hop. Caller is responsible for OData-escaping single quotes inside
    // string literals (double them) — see TrackingPage.buildTrackerFilter.
    get: (skip: number, top: number, filter?: string): Promise<any> => {
      const filterPart = filter ? `&$filter=${encodeURIComponent(filter)}` : ''
      return request(`/odata/Trackings?$count=true&$skip=${skip}&$top=${top}&$orderby=Name${filterPart}`)
    },
    post: (data: TrackingModel): Promise<TrackingModel> => request(`/odata/Trackings`, { method: 'post', data }),
    put: (Id: number, data: TrackingModel): Promise<any> => request(`/odata/Trackings(${Id})`, { method: 'patch', data }),
    delete: (Id: number): Promise<any> => request(`/odata/Trackings(${Id})`, { method: 'DELETE' })
  }
  public directMessage = {
    getLog: (offset: number, take: number): Promise<any> => request(`/api/v2/directmessages?offset=${offset}&take=${take}`),
    post: (data: DirectMessageModel): Promise<DirectMessageModel> => request(`/api/DirectMessageAPI/Add`, { method: 'POST', data, silent: true }),
    smsPost: (data: SMSMessageDto): Promise<SMSMessageDto> => request(`/api/SMSAPI/SendMessage`, { method: "POST", data }),
    remove: (id: number): Promise<any> => request(`/api/v2/directmessages/${id}`, { method: "DELETE" }),

  }
  public UserCourses = {
    get: (): Promise<any> => request('/api/v2/training/courses/currentuser')
  }
  public trainingMatrix = {
    getSummary: (userId?: number): Promise<any> => request(`/api/v2/training/GetSummary${userId ? `?userId=${userId}` : ''}`),
    policies: {
      getActive: (): Promise<any> => request('/odata/AcknowledgeableDocuments?$filter=IsActive'),
      remindAll: (policyId: number): Promise<any> => request(`/api/v2/training/policies/remindpolicy/${policyId}`, { method: 'get' }),
      remindUser: (policyId: number, userId: number): Promise<any> => request(`/api/v2/training/policies/remind`, { method: 'post', data: { UserId: userId, DocumentId: policyId } }),
      // Proper service-backed endpoints — handle CompanyId/CreatedByUserId/CreatedOn server-side.
      acknowledge:   (userId: number, policyId: number, certifiedOn?: string | null, expiryOn?: string | null): Promise<any> =>
        request(`/api/v2/training/policies/acknowledge`,   { method: 'post', data: { UserId: userId, DocumentId: policyId, CertifiedOn: certifiedOn || null, ExpiryOn: expiryOn || null } }),
      unacknowledge: (userId: number, policyId: number): Promise<any> => request(`/api/v2/training/policies/unacknowledge`, { method: 'post', data: { UserId: userId, DocumentId: policyId } }),
      exempt:        (userId: number, policyId: number): Promise<any> => request(`/api/v2/training/policies/exempt`,        { method: 'post', data: { UserId: userId, DocumentId: policyId } }),
      unexempt:      (userId: number, policyId: number): Promise<any> => request(`/api/v2/training/policies/unexempt`,      { method: 'post', data: { UserId: userId, DocumentId: policyId } }),
    },
    userAcknowledgedDocuments: {
      getAll: (): Promise<any> => request('/odata/UserAcknowledgedDocuments'),
      // Optimised: fetch only the two fields needed for the compliance matrix
      getAllSlim: (): Promise<any> => request('/odata/UserAcknowledgedDocuments?$select=UserId,AcknowledgeableDocumentsId'),
      getByUser: (userId: number): Promise<any> => request(`/odata/UserAcknowledgedDocuments?$filter=UserId eq ${userId}`),
      assign: (data: any): Promise<any> => request('/odata/UserAcknowledgedDocuments', { method: 'post', data }),
      delete: (id: number): Promise<any> => request(`/odata/UserAcknowledgedDocuments(${id})`, { method: 'delete' }),
    },
    userExemptedDocuments: {
      getAll: (): Promise<any> => request('/odata/UserExemptedDocuments'),
      // Optimised: fetch only the two fields needed for the compliance matrix
      getAllSlim: (): Promise<any> => request('/odata/UserExemptedDocuments?$select=UserId,DocumentId'),
      getByUser: (userId: number): Promise<any> => request(`/odata/UserExemptedDocuments?$filter=UserId eq ${userId}`),
      create: (data: any): Promise<any> => request('/odata/UserExemptedDocuments', { method: 'post', data }),
      delete: (id: number): Promise<any> => request(`/odata/UserExemptedDocuments(${id})`, { method: 'delete' }),
    },
    sendCertificationActionNotification: (data: {
      UserId: number;
      CertificationName: string;
      Status: string;
      CertifiedDate: string;
      ExpiryDate: string;
      ActionType: 'Added' | 'Updated' | 'Deleted';
    }): Promise<any> => request('/api/v2/training/certificates/sendActionNotification', { method: 'post', data }),
    sendPolicyActionNotification: (data: {
      UserId: number;
      CertificationName: string;
      Status: string;
      CertifiedDate: string;
      ExpiryDate: string;
      ActionType: 'Added' | 'Updated' | 'Deleted';
    }): Promise<any> => request('/api/v2/training/policies/sendActionNotification', { method: 'post', data }),
  }

  public companyPolicies = {
    getAll: (): Promise<any> => request('/odata/AcknowledgeableDocuments?$filter=IsActive'),
    create: (data: any): Promise<any> => request('/odata/AcknowledgeableDocuments', { method: 'post', data }),
    update: (id: number, data: any): Promise<any> => request(`/odata/AcknowledgeableDocuments(${id})`, { method: 'patch', data }),
    delete: (id: number): Promise<any> => request(`/odata/AcknowledgeableDocuments(${id})`, { method: 'delete' })
  }

  public export = {
    logFormExport: (formName: string): Promise<any> =>
      request('/api/ImportAPI/logFormExport', { method: 'post', data: JSON.stringify(formName), silent: true }),
  }

  public import = {
    people: {
      directImport: (data: File): Promise<any> => 
        request('/api/ImportAPI/people/directimport', { method: 'post', data }),
      directUpdate: (data: File): Promise<any> => 
        request('/api/ImportAPI/people/directupdate', { method: 'post', data }),
      getTemplate: (): Promise<{ content: string; filename: string }> =>
        request(`/api/ImportAPI/TemplatePeople/${this._getCompanyId()}/${this._getCurrentUserId()}`)
    },
    assets: {
      directImport: (data: File): Promise<any> => 
        request('/api/ImportAPI/assets/import', { method: 'post', data }),
      directUpdate: (data: File): Promise<any> => 
        request('/api/ImportAPI/assets/update', { method: 'post', data }),
      getTemplate: (): Promise<{ content: string; filename: string }> =>
        request(`/api/ImportAPI/TemplateAssets/${this._getCompanyId()}/${this._getCurrentUserId()}`)
    },
    peopleCertificates: {
      directUpdate: (data: File): Promise<any> =>
        request('/api/ImportAPI/peoplecertificates/directupdate', { method: 'post', data }),
      getTemplate: (): Promise<{ content: string; filename: string }> =>
        request(`/api/ImportAPI/TemplatePeopleCertificates/${this._getCompanyId()}/${this._getCurrentUserId()}`)
    }
  }

public ai = {
  chat: (data: { message: string; timezone?: string; language?: string }): Promise<any> =>
    request('/api/v2/chatbot/chat', { method: 'post', data, silent: true }),

  deleteSession: (): Promise<any> =>
    request('/api/v2/chatbot/session', { method: 'delete', silent: true }),
}

public notifications = {
  send: (data: InAppNotificationCreateDto): Promise<any> =>
    request('/api/v2/notifications', { method: 'post', data }),

  getAll: (offset: number = 0, take: number = 50): Promise<any> =>
    request('/api/v2/notifications', { params: { offset, take } }),

  getUserNotifications: (offset: number = 0, take: number = 50): Promise<any> =>
    request('/api/v2/notifications/user', { params: { offset, take } }),

  getUnreadCount: (): Promise<any> =>
    request('/api/v2/notifications/unread-count'),

  markAsRead: (recipientId: number): Promise<any> =>
    request(`/api/v2/notifications/${recipientId}/mark-read`, { method: 'post' }),

  markAllAsRead: (): Promise<any> =>
    request('/api/v2/notifications/mark-all-read', { method: 'post' }),

  delete: (notificationId: number): Promise<any> =>
    request(`/api/v2/notifications/${notificationId}`, { method: 'delete' }),
}

}
